import { RenderEtaReading } from './render-eta';
import { RenderExporter } from './render-export-guide';
import { RENDER_PREVIEW_ONE_FRAME, RENDER_PREVIEW_STAGES, RenderSampleId, RenderStills, RenderVideos } from './render.data';

// One model going through the render flow: upload → preview (a still in both orientations) →
// purchase → full 360° render → downloads. Steps map 1:1 to /render/<step>/:modelId.
export type RenderFlowStep =
  | 'processing'   // preview render in progress
  | 'preview'      // still frames ready, purchase offered
  | 'checkout'     // payment (stub until the backend exists)
  | 'confirming'   // waiting for the payment to be confirmed
  | 'rendering'    // full 360° render after payment
  | 'downloads'    // both videos ready
  | 'error';       // preview or render failed; upload again or contact support

export const RENDER_FLOW_STEPS: ReadonlyArray<RenderFlowStep> = ['processing', 'preview', 'checkout', 'confirming', 'rendering', 'downloads', 'error'];

// Where the model came from. An upload keeps a summary only: File objects do not survive a
// reload. They go to the render backend under the `renderLive` flag, nowhere without it
// (docs/render-landing-port.md).
export type RenderJobSource =
  | { kind: 'sample'; sampleId: RenderSampleId }
  | {
    kind: 'upload';
    fileName: string;
    format: string;
    fileCount: number;
    textureCount: number;
    totalBytes: number;
    // What the FBX itself says (render-fbx-textures.ts), when it could be read: how many textures
    // it asks for or carries — 0 puts the export guide's line under the preview — and the program
    // that wrote it.
    modelTextures?: number;
    exporter?: RenderExporter;
  };

// 'preview' = the free still; 'full' = videos paid for ($9 for everyone, members too: owner,
// 2026-10-14).
export type RenderEntitlement = 'preview' | 'full';

// Each orientation's frame on a page: blurred or not.
export interface RenderVeil {
  landscape: boolean;
  portrait: boolean;
}

// A paid model whose frame still carries the preview's watermark — its clean one comes with the paid
// run, a few minutes in: the frame is shown blurred until then, and the blur lifts on the clean one
// (owner, 2026-11-02: "заблюрить превьюшку … как только можем забрать настоящий кадр — показываем").
export function renderFrameVeiled(job: RenderJob | null | undefined, side: keyof RenderVeil): boolean {
  return !!job && job.entitlement === 'full' && !!job.preview?.watermarked?.[side];
}

// Whose failure it is, which decides what the page says (proposals 2026-09-30): the model's files
// ('model': fix them and upload again), ours ('service': nothing is wrong with the model, upload it
// again), or the account's limits ('account').
export type RenderFailureBlame = 'model' | 'service' | 'account';

export interface RenderJobError {
  title: string;
  text: string;
  // Which step failed: the preview or the paid videos.
  failedStep: 'processing' | 'rendering';
  // Unknown on errors stored by older builds: treated as ours.
  blame?: RenderFailureBlame;
  // How far the run had come, for "stopped at 62%".
  progress?: number;
  // The renderer could not read the model's textures: the page links the export guide.
  about?: 'textures';
}

export interface RenderJob {
  modelId: string;
  name: string;
  source: RenderJobSource;
  step: RenderFlowStep;
  // 0–100 for processing / rendering: the bar exactly as every place shows it (the card, the
  // header's ring, the menu, the library, the tab), and it never goes back within a step. A
  // preview's counts its upload (RENDER_UPLOAD_BAR) and its stages (RENDER_PREVIEW_STAGES).
  progress: number;
  statusText: string;
  entitlement: RenderEntitlement;
  preview?: RenderStills;
  videos?: RenderVideos;
  error?: RenderJobError;
  // The sample whose stills stand in for an upload's preview (uploads are not processed yet).
  standInSampleId?: RenderSampleId;
  // The model on the backend once its upload is done: the id of its Firestore document
  // (userdata/{uid}/models/{id}, the file's name without the extension, as the library's uploads).
  // A reload follows that document again instead of starting over.
  backendId?: string;
  // Last checkout attempt failed ('declined') — shown on the checkout step until the next try.
  checkoutError?: string;
  // The Stripe checkout last opened for this model (the dev render backend): the return from
  // Stripe names the session, and this is how a tab without the job finds its model.
  checkout?: { sessionId: string; at: number };
  // The upload on its way (processing, before the render): its bytes so far. The job exists from
  // the upload's first byte, so its page, the header and the library show it from then on.
  uploading?: { sentBytes: number; totalBytes: number };
  // What the upload page found worth saying about the files ("From big.zip: 1 model and 14
  // textures. Using rock_LOD0.fbx, the most detailed of 6 versions."): under the model's screens.
  uploadNote?: string;
  // Each orientation's preview as the renderer reports it (the dev render backend only; the mock
  // has one progress for both).
  sides?: RenderSides;
  // How far each video's frames stand, for the time left (the render backend only; render-eta.ts).
  eta?: RenderEtaReading;
  // A run ended (preview, videos, or a failure) while the user was elsewhere; cleared once they
  // open the model. Its library card carries a blue dot until then.
  unseen?: boolean;
  createdAt: number;
  updatedAt: number;
}

// Where one orientation's preview stands: its own GPU job goes through these in order (measured on
// the dev renderer, 2026-10-01, ~18 s each): waiting for a GPU, loading the model, rendering the
// frame, saving it, done.
export type RenderSidePhase = 'waiting' | 'loading' | 'rendering' | 'saving' | 'done';

export interface RenderSides {
  landscape: RenderSidePhase;
  portrait: RenderSidePhase;
  // A finished orientation's frame, shown on its screen while the other one renders.
  landscapeFrame?: string;
  portraitFrame?: string;
}

const SIDE_PHASES: ReadonlyArray<RenderSidePhase> = ['waiting', 'loading', 'rendering', 'saving', 'done'];

// The processing card's current stage (an index into RENDER_PREVIEW_STAGES) from the two GPU jobs.
// The orientation further along decides the first two (decision 40): one job started is the studio
// set up (`podStarted` on is 'loading'), and one job that opened the model and rendered from it
// has checked it — both open the same file. The frames are both or neither: rendering until the
// slower one is saved too.
export function renderPreviewStage(sides: RenderSides): number {
  const ahead = Math.max(SIDE_PHASES.indexOf(sides.landscape), SIDE_PHASES.indexOf(sides.portrait));
  return Math.min(Math.max(ahead, 0), 2);
}

// Where the renderer's report puts the preview's bar: the start of the card's current stage, or
// three quarters once one of the two frames is saved.
export function renderPreviewFloor(sides: RenderSides): number {
  const stage = renderPreviewStage(sides);
  const framesDone = [sides.landscape, sides.portrait].filter((phase) => phase === 'done').length;
  return stage === 2 && framesDone === 1 ? RENDER_PREVIEW_ONE_FRAME : RENDER_PREVIEW_STAGES[stage].from;
}

// Where the bar has crept `elapsedMs` after it reached `floor` with nothing new reported: steadily
// to nine tenths of the stage's share by its usual time, then ever slower towards the next stage's
// start, which only the renderer's next report crosses. The frames creep towards the first one
// saved (RENDER_PREVIEW_ONE_FRAME), then towards 99: 100 is the preview.
export function renderPreviewCreep(floor: number, elapsedMs: number): number {
  const index = RENDER_PREVIEW_STAGES.reduce((current, stage, i) => (floor >= stage.from ? i : current), 0);
  const last = index === RENDER_PREVIEW_STAGES.length - 1;
  const oneFrame = last && floor >= RENDER_PREVIEW_ONE_FRAME;
  const end = !last ? RENDER_PREVIEW_STAGES[index + 1].from : oneFrame ? 99 : RENDER_PREVIEW_ONE_FRAME;
  // The other frame is a parallel job: it usually lands within half the time the first took.
  const expectedMs = oneFrame ? RENDER_PREVIEW_STAGES[index].expectedMs / 2 : RENDER_PREVIEW_STAGES[index].expectedMs;
  const elapsed = Math.max(0, elapsedMs);
  const share = elapsed <= expectedMs ? 0.9 * (elapsed / expectedMs) : 0.9 + 0.1 * (1 - Math.exp(-(elapsed - expectedMs) / expectedMs));
  return Math.max(floor, Math.min(end - 1, Math.floor(floor + (end - floor) * share)));
}

// Each video, as the line under the videos' bar speaks of it (owner, 2026-10-21): the two are
// separate GPU jobs that start, render and finish apart — one was at 83% while the other's machine
// never came (dev, 21.10). Not begun yet, the scene set up (~25 s), its frames rendered, the video
// put together, done.
export type RenderVideoPhase = 'waiting' | 'preparing' | 'rendering' | 'finishing' | 'done';

export interface RenderVideoSide {
  phase: RenderVideoPhase;
  // Its frames rendered, 0–100 (while 'rendering').
  frames: number;
}

// What each phase gives the bar: an average of the two videos, the frames 5 → 90.
const VIDEO_BAR: Readonly<Record<RenderVideoPhase, number>> = { waiting: 0, preparing: 5, rendering: 5, finishing: 95, done: 100 };
const VIDEO_FRAMES_BAR = 85;

export function renderVideosProgress(landscape: RenderVideoSide, portrait: RenderVideoSide): number {
  const side = ({ phase, frames }: RenderVideoSide) => VIDEO_BAR[phase] + (phase === 'rendering' ? (VIDEO_FRAMES_BAR * frames) / 100 : 0);
  return Math.floor((side(landscape) + side(portrait)) / 2);
}

// The one line under the videos' bar: the two videos as one thing, never one of them named or said
// to be ahead (the boss, 2026-10-22; owner: one artifact, the short words, 2026-10-23). Setting up
// while neither has begun its frames; finishing once both are past them; rendering in between.
// Failures are the error step's.
export function renderVideosStatus(landscape: RenderVideoPhase, portrait: RenderVideoPhase): string {
  const both = (phases: ReadonlyArray<RenderVideoPhase>) => phases.includes(landscape) && phases.includes(portrait);
  if (both(['waiting', 'preparing'])) {
    return RENDER_VIDEO_LINES.setup;
  }
  if (both(['done'])) {
    return RENDER_VIDEO_LINES.ready;
  }
  return both(['finishing', 'done']) ? RENDER_VIDEO_LINES.finishing : RENDER_VIDEO_LINES.rendering;
}

export const RENDER_VIDEO_LINES = { setup: 'Setting up…', rendering: 'Rendering…', finishing: 'Finishing…', ready: 'Ready' } as const;

// Progress reports from the backend while a preview or a full render runs.
export interface RenderProgress {
  progress: number;
  statusText: string;
}

export interface RenderPreviewResult {
  preview: RenderStills;
  standInSampleId?: RenderSampleId;
}

export interface RenderVideosResult {
  videos: RenderVideos;
  // The full render's own stills, without the preview's watermark, where the backend has them.
  preview?: RenderStills;
}

// What a checkout hands back. The dev render backend: a Stripe Checkout URL to redirect to (Stripe
// returns to /success?session_id=… or /cancel, RenderCheckoutReturnGuard). The stub opens
// /render/checkout instead.
export interface RenderCheckoutSession {
  sessionId: string;
  url?: string;
}

// What a pack download hands the browser to save. The dev render backend: one ZIP of the chosen
// platform files (render_download). The mock and the samples: the source videos standing in for it
// (`standIn`), as nothing is encoded here.
export interface RenderPackFiles {
  files: RenderPackFile[];
  standIn: boolean;
}

export interface RenderPackFile {
  url: string;
  name: string;
  // What the button calls it when it is the next one to save: "portrait video".
  label: string;
}

// What the two videos weigh, in bytes, as the backend has them (render_download_info).
export interface RenderPackSizes {
  portrait: number;
  landscape: number;
}

// A short notice the next step shows once (prototype's flowMessage): "No payment was taken." etc.
export interface RenderFlowNotice {
  text: string;
  tone: 'info' | 'error' | 'success';
}

// The model's document went away while the page followed it: cancelled in another tab or window.
export class RenderModelGoneError extends Error {
  constructor() {
    super('This model is no longer on the server. Upload it again.');
    this.name = 'RenderModelGoneError';
  }
}

// A preview or a render that failed, with whose failure it is.
export class RenderRunError extends Error {
  constructor(message: string, public readonly blame: RenderFailureBlame, public readonly about?: 'textures') {
    super(message);
    this.name = 'RenderRunError';
  }
}

// The checkout could not be opened, in words the page can show as they are.
export class RenderCheckoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RenderCheckoutError';
  }
}

export class RenderNotImplementedError extends Error {
  constructor(what: string) {
    super(`${what} is not implemented yet — backend work pending, see docs/render-landing-port.md`);
    this.name = 'RenderNotImplementedError';
  }
}
